/**
 * ReactFlowRetrievalMap.tsx
 *
 * @xyflow/react v12 + @dagrejs/dagre v3 implementation of the retrieval map.
 * SVG/HTML renderer — nodes are rich React component cards.
 *
 * Feature branch: feature/reactflow-graph
 * Purpose: side-by-side comparison with the sigma.js WebGL implementation.
 *
 * Props interface intentionally matches SigmaRetrievalMap exactly so it can
 * be swapped in with zero parent changes.
 *
 * Phase 2a visual redesign (2026-07-01):
 * - Field background: #101012 (--bg-0) to match app; vignette overlay
 * - Dot grid: #2e2e33 (--line token), gap 22, size 1
 * - Node glow model: 3-layer box-shadow scaled by relevance score
 * - Card fill: #1c1c1f (--bg-2 token)
 * - Dual-layer edges: normative solid animated, informative dashed static
 * - Footer caption: "Searched N domains · used top N · N below the line · N referenced"
 * - Query node: thin top rail (~28px tall)
 * - Domain colours: imported from domainPalette.ts (family-clustered)
 * - focusNode() method exposed via forwardRef for [N] citation → map pan
 */

import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Handle,
  Position,
  BaseEdge,
  getSmoothStepPath,
  MarkerType,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodeId,
  type Node,
  type Edge,
  type NodeProps,
  type EdgeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import dagre from '@dagrejs/dagre';

import type { RetrievalTree, RetrievalTreeClause, PageInfo } from '../types';
import { getDomainColor, DEFAULT_DOMAIN_COLOR } from '../domainPalette';
import { fetchClauseFull, fetchClausePages, getPageImageUrl, requestPageMapping, fetchClausesBatch } from '../api';
// TODO(batch-expand): fetchClausesBatch is imported and ready.
// To wire "expand all in this group" on StandardGroupNode:
//   1. Add a `clauseIds: string[]` field to GroupData (populated in buildRFGraph)
//   2. Add an onExpandGroup callback to MapContext + StandardGroupNode props
//   3. In the handler: call fetchClausesBatch(data.clauseIds) then update expanded state
// The POST /api/query/expand endpoint is live; wiring is straightforward once GroupData has IDs.
import { clearClauseDetailCache } from '../hooks/useClauseDetail';
import type { ClauseFull } from '../hooks/useClauseDetail';
import ClauseReader from './ClauseReader';

/* ─── Clause detail cache (module-level, survives re-renders) ───────────── */

interface ClauseDetailEntry { full?: ClauseFull; pages?: PageInfo[]; }
const clauseDetailCache = new Map<string, ClauseDetailEntry>();

/* ─── ExpandNodeParams (local — not yet exported from types.ts) ──────────── */

interface ExpandNodeParams {
  nodeId:   string;
  nodeType: string;
  axis:     string;
}

/* ─── Relevance score helper ─────────────────────────────────────────────── */

function scorePercent(clause: RetrievalTreeClause): number {
  const raw = clause.rerank_score ?? Math.min(clause.rrf_score * 5, 1);
  return Math.round(Math.max(0, Math.min(1, raw)) * 100);
}

/* ─── Glow radius based on relevance tier (§1.4 spec) ───────────────────── */

function glowRadius(score: number): number {
  if (score >= 85) return 34; // top tier
  if (score >= 55) return 20; // mid tier
  if (score >= 25) return 12; // low tier
  return 6;                   // cut tier
}

/* ─── Border width based on relevance tier ──────────────────────────────── */

function borderWidth(score: number): number {
  if (score >= 85) return 2.5;
  if (score >= 55) return 1.75;
  if (score >= 25) return 1.25;
  return 1;
}

/* ─── Layout constants ───────────────────────────────────────────────────── */

const CLAUSE_W        = 176;
const CLAUSE_H        = 88;
const CLAUSE_GAP      = 10;
const GROUP_PAD_X     = 14;
const GROUP_PAD_Y     = 8;
const GROUP_HEADER_H  = 30;
const MAX_COLS        = 4;

// Query node is now a thin rail — full width of the canvas, ~28px tall
// Width is set dynamically based on the actual graph width; use a placeholder here
const QUERY_RAIL_H    = 32;
const QUERY_RAIL_W    = 240; // dagre placeholder; actual width set via style

// Phase 2c: Ghost / absent node dimensions
const GHOST_W            = 120;
const GHOST_H            = 54;
const GHOST_GAP          = 16;   // vertical gap between ghost cards
const GHOST_RIGHT_OFFSET = 160;  // horizontal gap between rightmost group and ghost column

function calcGroupDims(clauseCount: number): { w: number; h: number; cols: number; rows: number } {
  const cols = Math.min(clauseCount, MAX_COLS);
  const rows = Math.ceil(clauseCount / cols);
  const w    = cols * CLAUSE_W + (cols - 1) * CLAUSE_GAP + 2 * GROUP_PAD_X;
  const h    = rows * CLAUSE_H + (rows - 1) * CLAUSE_GAP + 2 * GROUP_PAD_Y + GROUP_HEADER_H;
  return { w, h, cols, rows };
}

/* ─── Node / Edge data types ─────────────────────────────────────────────── */

export type ClauseData = {
  clause:      RetrievalTreeClause;
  domainColor: string;
  score:       number;
};

export type GroupData = {
  standardId:   string;
  standardName: string;
  domainColor:  string;
  clauseCount:  number;
  /** Number of ghost/absent nodes that reference a clause in this group (Phase 2c) */
  ghostCount:   number;
};

export type QueryData = {
  label:       string;
  clauseCount: number;
  domainCount: number;
  reranked:    boolean;
};

export type CrossRefData = {
  refType: 'normative' | 'informative';
};

/**
 * Phase 2c — ghost/absent node data.
 * kind='ghost'  → clause exists in standards but was not retrieved (cross_ref_only)
 * kind='absent' → clause is not in the corpus at all (not_in_corpus)
 */
export type GhostData = {
  notRetrieved: {
    id:            string | null;
    standard:      string;
    title?:        string;
    reason:        string;
    referenced_by: string[];
  };
  domainColor: string;
  kind:        'ghost' | 'absent';
};

type ClauseRFNode   = Node<ClauseData,   'clause'>;
type GroupRFNode    = Node<GroupData,    'standardGroup'>;
type QueryRFNode    = Node<QueryData,    'query'>;
type GhostRFNode    = Node<GhostData,    'ghost'>;
type AnyRFNode      = ClauseRFNode | GroupRFNode | QueryRFNode | GhostRFNode;
type CrossRefRFEdge = Edge<CrossRefData, 'crossRef'>;

/* ─── Flow context (callbacks, highlight — avoids prop-drilling) ─────────── */

interface FlowCtxValue {
  highlightedNodeId: string | null;
  expandedNodeId:    string | null;
  dimmedNodeIds:     Set<string>;
  onClauseSelect:    (id: string) => void;
  onExpandClause:    (id: string | null) => void;
  onHoverClause?:    (id: string | null) => void;
  onExpandNode?:     (params: ExpandNodeParams) => void;
  onOpenReader?:     (clauseId: string, page?: number) => void;
  navStack:          string[];
  onNavTo:           (targetId: string) => void;
  // Phase 2c additions
  /** All cross-refs from the current tree (for Tier 1 cross-ref chips) */
  crossRefs:         Array<{ from_clause_id: string; to_clause_id: string; ref_type?: string }>;
  /** Actual clause IDs that have ghost RF nodes in the graph */
  ghostNodeIds:      Set<string>;
  /** Map from actual clause ID → short display label */
  clauseLabels:      Map<string, string>;
  /** Triggers ghost materialise (fetch + insert as real clause) */
  onMaterialise:     (nodeId: string, nr: GhostData['notRetrieved']) => void;
  /** Triggers absent-node corpus request */
  onRequestAbsent:   (nodeId: string, standard: string) => void;
  /** RF node ID currently being materialised (shows spinner) */
  ghostLoadingId:    string | null;
  /** RF node ID currently in error state (1.5 s flash) */
  ghostErrorId:      string | null;
}
const FlowCtx = createContext<FlowCtxValue>({
  highlightedNodeId: null,
  expandedNodeId:    null,
  dimmedNodeIds:     new Set(),
  onClauseSelect:    () => {},
  onExpandClause:    () => {},
  navStack:          [],
  onNavTo:           () => {},
  crossRefs:         [],
  ghostNodeIds:      new Set(),
  clauseLabels:      new Map(),
  onMaterialise:     () => {},
  onRequestAbsent:   () => {},
  ghostLoadingId:    null,
  ghostErrorId:      null,
});

/* ─── Ghost node ID helper (Phase 2c) ───────────────────────────────────── */

/**
 * Derives a stable RF node ID for a not_retrieved entry.
 * Uses "ghost-{clauseId}" for cross_ref_only, "ghost-absent-{std}-{idx}" for not_in_corpus.
 */
function makeGhostId(nr: RetrievalTree['not_retrieved'][number], idx: number): string {
  if (nr.id) return `ghost-${nr.id}`;
  return `ghost-absent-${(nr.standard || 'unknown').replace(/[^a-zA-Z0-9]/g, '-')}-${idx}`;
}

/* ─── dagre layout builder ───────────────────────────────────────────────── */

interface LayoutResult {
  nodes: AnyRFNode[];
  edges: Edge[];
}

function buildLayout(tree: RetrievalTree): LayoutResult {
  const nodes: AnyRFNode[] = [];
  const edges: Edge[]      = [];

  /* Group clauses by standard ─────────────────────────────────────────── */
  const standardGroups = new Map<string, RetrievalTreeClause[]>();
  for (const clause of tree.clauses) {
    const existing = standardGroups.get(clause.standard) ?? [];
    existing.push(clause);
    standardGroups.set(clause.standard, existing);
  }

  /* Sort each group by score desc ─────────────────────────────────────── */
  for (const [, clauses] of standardGroups) {
    clauses.sort((a, b) => scorePercent(b) - scorePercent(a));
  }

  /* Build a map: clause.id → standard id ─────────────────────────────── */
  const clauseToStandard = new Map<string, string>();
  for (const [std, clauses] of standardGroups) {
    for (const c of clauses) clauseToStandard.set(c.id, std);
  }

  /* dagre: LR layout — query node as root ────────────────────────────── */
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'LR',
    ranksep: 80,
    nodesep: 60,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  /* Add query rail node — dagre placeholder, visual width set via CSS ─── */
  g.setNode('__query__', { width: QUERY_RAIL_W, height: QUERY_RAIL_H });

  /* Add standard group nodes ──────────────────────────────────────────── */
  for (const [std, clauses] of standardGroups) {
    const { w, h } = calcGroupDims(clauses.length);
    g.setNode(std, { width: w, height: h });
  }

  /* Query→group edges so dagre knows the spine ───────────────────────── */
  for (const std of standardGroups.keys()) {
    g.setEdge('__query__', std);
  }

  /* Add inter-standard cross-ref edges so dagre can route them ────────── */
  const addedDagreEdges = new Set<string>();
  for (const xref of tree.cross_refs ?? []) {
    const fromStd = clauseToStandard.get(xref.from_clause_id);
    const toStd   = clauseToStandard.get(xref.to_clause_id);
    if (fromStd && toStd && fromStd !== toStd) {
      const key = `${fromStd}→${toStd}`;
      if (!addedDagreEdges.has(key)) {
        addedDagreEdges.add(key);
        g.setEdge(fromStd, toStd);
      }
    }
  }

  dagre.layout(g);

  /* Phase 2c: Pre-compute ghost count per standard group ──────────────────
   * ghostCount = how many renderable not_retrieved entries have at least one
   * referenced_by clause that lives in this standard group.                 */
  const renderableNR = (tree.not_retrieved ?? []).filter(
    nr => nr.reason === 'cross_ref_only' || nr.reason === 'not_in_corpus'
  );
  const ghostCountPerStd = new Map<string, number>();
  for (const nr of renderableNR) {
    const counted = new Set<string>();
    for (const refId of nr.referenced_by ?? []) {
      const refStd = clauseToStandard.get(refId);
      if (refStd && !counted.has(refStd)) {
        counted.add(refStd);
        ghostCountPerStd.set(refStd, (ghostCountPerStd.get(refStd) ?? 0) + 1);
      }
    }
  }

  /* Build query rail node ─────────────────────────────────────────────── */
  const qdn = g.node('__query__') as { x: number; y: number; width: number; height: number };
  nodes.push({
    id:       '__query__',
    type:     'query',
    position: { x: qdn.x - qdn.width / 2, y: qdn.y - qdn.height / 2 },
    data: {
      label:       (tree as { query?: string }).query ?? 'Query',
      clauseCount: tree.clauses.length,
      domainCount: tree.domains.length,
      reranked:    tree.meta?.reranked ?? false,
    },
    draggable:  true,
    selectable: true,
  } as QueryRFNode);

  /* Build React Flow nodes from dagre output ──────────────────────────── */
  for (const [std, clauses] of standardGroups) {
    const dn  = g.node(std) as { x: number; y: number; width: number; height: number };
    const gx  = dn.x - dn.width  / 2;
    const gy  = dn.y - dn.height / 2;

    /* Determine domain colour from first clause ─────────────────────── */
    const domainColor = clauses[0] ? getDomainColor(clauses[0].domain) : DEFAULT_DOMAIN_COLOR;

    /* Standard group node ─────────────────────────────────────────────── */
    nodes.push({
      id:       std,
      type:     'standardGroup',
      position: { x: gx, y: gy },
      style:    { width: dn.width, height: dn.height },
      data: {
        standardId:   std,
        standardName: std,
        domainColor,
        clauseCount:  clauses.length,
        ghostCount:   ghostCountPerStd.get(std) ?? 0,
      },
      draggable:  true,
      selectable: true,
    } as GroupRFNode);

    /* Clause child nodes (positions relative to parent) ─────────────── */
    const { cols } = calcGroupDims(clauses.length);
    clauses.forEach((clause, idx) => {
      const col  = idx % cols;
      const row  = Math.floor(idx / cols);
      const relX = GROUP_PAD_X + col * (CLAUSE_W + CLAUSE_GAP);
      const relY = GROUP_HEADER_H + GROUP_PAD_Y + row * (CLAUSE_H + CLAUSE_GAP);

      nodes.push({
        id:       clause.id,
        type:     'clause',
        parentId: std,
        position: { x: relX, y: relY },
        data: {
          clause,
          domainColor: getDomainColor(clause.domain),
          score:       scorePercent(clause),
        },
        draggable:  false,
        selectable: true,
      } as ClauseRFNode);
    });
  }

  /* Query→group React Flow edges (structural spine) ────────────────────── */
  for (const std of standardGroups.keys()) {
    edges.push({
      id:     `query-${std}`,
      source: '__query__',
      target: std,
      type:   'default',
      style: {
        stroke:      'rgba(99,102,241,0.28)',
        strokeWidth: 1,
      },
    } as Edge);
  }

  /* React Flow cross-ref edges — normative solid, informative dashed ────── */
  const addedEdges = new Set<string>();
  for (const xref of tree.cross_refs ?? []) {
    if (
      !clauseToStandard.has(xref.from_clause_id) ||
      !clauseToStandard.has(xref.to_clause_id)
    ) continue;

    const edgeId = `xref-${xref.from_clause_id}-${xref.to_clause_id}`;
    if (addedEdges.has(edgeId)) continue;
    addedEdges.add(edgeId);

    // Treat all cross-refs as normative if ref_type is not set (Phase 2a)
    const refType = xref.ref_type ?? 'normative';
    const isNormative = refType === 'normative';

    edges.push({
      id:       edgeId,
      source:   xref.from_clause_id,
      target:   xref.to_clause_id,
      type:     'crossRef',
      animated: false,  // animation handled via CSS in CrossRefEdge component
      zIndex:   5,
      data:     { refType },
      style: isNormative
        ? {
            stroke:      'rgba(167,139,250,0.70)',  // #a78bfa @ 70%
            strokeWidth: 1.75,
          }
        : {
            stroke:      'rgba(139,92,246,0.45)',   // #8B5CF6 @ 45%
            strokeWidth: 1.25,
            strokeDasharray: '4 4',
          },
      markerEnd: {
        type:   MarkerType.ArrowClosed,
        color:  isNormative ? 'rgba(167,139,250,0.70)' : 'rgba(139,92,246,0.45)',
        width:  12,
        height: 12,
      },
    } as CrossRefRFEdge);
  }

  /* Phase 2c: Ghost / absent nodes from not_retrieved ────────────────────
   * Placed in a column to the right of the rightmost standard group.
   * Only cross_ref_only (ghost) and not_in_corpus (absent) are rendered;
   * topk_limit / below_threshold / domain_filtered are skipped.             */

  // Find the rightmost edge of all rendered standard groups
  let maxGroupRightX = 0;
  for (const [std] of standardGroups) {
    const dn = g.node(std) as { x: number; y: number; width: number; height: number };
    const right = dn.x + dn.width / 2;
    if (right > maxGroupRightX) maxGroupRightX = right;
  }

  // Build a set of all clause IDs that are actually in the rendered graph
  const renderedClauseIds = new Set(tree.clauses.map(c => c.id));

  renderableNR.forEach((nr, idx) => {
    const ghostId     = makeGhostId(nr, idx);
    const kind: 'ghost' | 'absent' = nr.reason === 'not_in_corpus' ? 'absent' : 'ghost';
    const ghostX      = maxGroupRightX + GHOST_RIGHT_OFFSET;
    const ghostY      = 20 + idx * (GHOST_H + GHOST_GAP);
    const referencedBy = nr.referenced_by ?? [];

    nodes.push({
      id:       ghostId,
      type:     'ghost',
      position: { x: ghostX, y: ghostY },
      data: {
        notRetrieved: {
          id:            nr.id,
          standard:      nr.standard,
          title:         nr.title,
          reason:        nr.reason,
          referenced_by: referencedBy,
        },
        domainColor: DEFAULT_DOMAIN_COLOR,
        kind,
      },
      draggable:  true,
      selectable: true,
    } as GhostRFNode);

    // Dashed edges from each referenced_by clause (if in graph) → ghost node
    for (const refId of referencedBy) {
      if (renderedClauseIds.has(refId)) {
        edges.push({
          id:     `ghost-edge-${refId}-${ghostId}`,
          source: refId,
          target: ghostId,
          type:   'default',
          style: {
            stroke:          '#52525b',
            strokeWidth:     1,
            strokeDasharray: '4 4',
          },
          markerEnd: {
            type:   MarkerType.ArrowClosed,
            color:  '#52525b',
            width:  10,
            height: 10,
          },
        } as Edge);
      }
    }
  });

  return { nodes, edges };
}

/* ─── QueryNode — thin top rail ──────────────────────────────────────────── */

const QueryNode = memo(function QueryNode({ data }: NodeProps<QueryRFNode>) {
  const { label, clauseCount, domainCount, reranked } = data;

  const truncated = label.length > 70 ? label.slice(0, 67) + '…' : label;
  const subtitle = `${clauseCount} clause${clauseCount !== 1 ? 's' : ''} · ${domainCount} domain${domainCount !== 1 ? 's' : ''}${reranked ? ' · reranked' : ''}`;

  return (
    <div
      style={{
        width:           QUERY_RAIL_W,
        height:          QUERY_RAIL_H,
        borderRadius:    6,
        border:          '1px solid rgba(99,102,241,0.6)',
        background:      'rgba(99,102,241,0.08)',
        boxSizing:       'border-box',
        padding:         '0 12px',
        display:         'flex',
        flexDirection:   'row',
        alignItems:      'center',
        gap:             8,
        backdropFilter:  'blur(4px)',
      }}
    >
      {/* Right-side source handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />
      {/* Left-side target handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />

      {/* Diamond glyph */}
      <span style={{
        fontSize:   11,
        color:      'rgba(139,148,255,0.8)',
        flexShrink: 0,
        lineHeight: 1,
      }}>◇</span>

      {/* Query text */}
      <span style={{
        fontSize:     10,
        fontWeight:   600,
        color:        'rgba(139,148,255,1)',
        lineHeight:   1.3,
        overflow:     'hidden',
        textOverflow: 'ellipsis',
        whiteSpace:   'nowrap',
        flex:         1,
        minWidth:     0,
      }}>
        {truncated}
      </span>

      {/* Counts */}
      <span style={{
        fontSize:    9,
        color:       'rgba(99,102,241,0.7)',
        fontFamily:  'ui-monospace, monospace',
        flexShrink:  0,
        whiteSpace:  'nowrap',
      }}>
        {subtitle}
      </span>

      {/* Expand glyph placeholder */}
      <span style={{
        fontSize:    9,
        color:       'rgba(99,102,241,0.5)',
        flexShrink:  0,
      }}>⤢</span>
    </div>
  );
});

/* ─── Provenance label helper ───────────────────────────────────────────── */

function provenanceLabel(source: string | undefined): string {
  switch (source) {
    case 'vector':          return 'semantic match';
    case 'fts':             return 'keyword match';
    case 'forced':          return 'included by rule';
    case 'cross_ref_only':  return 'referenced only';
    default:                return source ?? 'retrieved';
  }
}

/* ─── ClauseNode ─────────────────────────────────────────────────────────── */

const ClauseNode = memo(function ClauseNode({ data }: NodeProps<ClauseRFNode>) {
  const { clause, domainColor, score } = data;
  const nodeId   = useNodeId() ?? clause.id;
  const ctx      = useContext(FlowCtx);
  const isHi     = ctx.highlightedNodeId === nodeId;
  const isExpanded = ctx.expandedNodeId === nodeId;
  const isDimmed = ctx.dimmedNodeIds.has(nodeId);

  const isCut    = clause.rrf_score < 0.01 || clause.retrieval_source === 'cross_ref_only';

  // Detail fetch state for expanded card
  const [detail, setDetail] = useState<{ full?: ClauseFull; pages?: PageInfo[]; loading: boolean }>({ loading: false });

  // F39: edge-aware expanded-panel placement — open up/left when the default
  // down/right position would clip at the flow container edge.
  const nodeRef = useRef<HTMLDivElement>(null);
  const [panelSide, setPanelSide] = useState<{ up: boolean; right: boolean }>({ up: false, right: false });
  useEffect(() => {
    if (!isExpanded) return;
    const el = nodeRef.current;
    const flow = el?.closest('.react-flow');
    if (!el || !flow) return;
    const nr = el.getBoundingClientRect();
    const fr = flow.getBoundingClientRect();
    const zoom = nr.height / CLAUSE_H; // panel is in node-local (zoomed) coords
    const panelH = 430 * zoom, panelW = 320 * zoom;
    setPanelSide({
      up: nr.bottom + panelH > fr.bottom && nr.top - fr.top > panelH,
      right: nr.left + panelW > fr.right,
    });
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const cached = clauseDetailCache.get(nodeId);
    if (cached?.full && cached?.pages) {
      setDetail({ full: cached.full, pages: cached.pages, loading: false });
      return;
    }
    setDetail(prev => ({ ...prev, loading: true }));
    Promise.all([
      cached?.full ? Promise.resolve(cached.full) : fetchClauseFull(nodeId),
      cached?.pages ? Promise.resolve(cached.pages) : fetchClausePages(nodeId),
    ]).then(([full, pages]) => {
      clauseDetailCache.set(nodeId, { full, pages });
      setDetail({ full, pages, loading: false });
    }).catch(() => setDetail(prev => ({ ...prev, loading: false })));
  }, [isExpanded, nodeId]);

  // Glow model: scale radius with score tier
  const glow    = glowRadius(isCut ? 0 : score);
  const bWidth  = borderWidth(isCut ? 0 : score);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCut && ctx.onExpandNode) {
      ctx.onExpandNode({ nodeId, nodeType: 'cut', axis: 'hierarchy' });
    } else {
      ctx.onExpandClause(isExpanded ? null : nodeId);
    }
  }, [isCut, ctx, nodeId, isExpanded]);

  const handleMouseEnter = useCallback(() => ctx.onHoverClause?.(nodeId), [ctx, nodeId]);
  const handleMouseLeave = useCallback(() => ctx.onHoverClause?.(null),   [ctx]);

  // 3-layer glow box-shadow (spec §1.2)
  const boxShadow = [
    `0 0 0 1px color-mix(in srgb, ${domainColor} 30%, transparent)`,
    `0 0 12px -2px color-mix(in srgb, ${domainColor} 22%, transparent)`,
    `0 0 ${glow}px 0 color-mix(in srgb, ${domainColor} 14%, transparent)`,
    ...(isHi || isExpanded ? [`0 0 0 2px rgba(99,102,241,0.9)`, `0 0 20px rgba(99,102,241,0.36)`] : []),
  ].join(', ');

  // Outgoing cross-refs for this node
  const outgoingXrefs = useMemo(() => {
    if (!isExpanded) return [];
    // We don't have tree here — cross-refs come from data props.
    // For now, return empty; the inner component renders them from context via onNavTo.
    return [];
  }, [isExpanded]);

  return (
    <div
      ref={nodeRef}
      className="rf-clause-node"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        width:        CLAUSE_W,
        height:       CLAUSE_H,
        borderColor:  domainColor,
        borderWidth:  bWidth,
        borderStyle:  isCut ? 'dashed' : 'solid',
        borderRadius: 6,
        boxShadow,
        background:   isCut ? '#161618' : '#1c1c1f',
        padding:      '7px 10px',
        cursor:       'pointer',
        userSelect:   'none',
        position:     'relative',
        overflow:     'visible',  // allow expanded panel to overflow
        boxSizing:    'border-box',
        transition:   'box-shadow 150ms ease-out, border-width 150ms ease-out, opacity 180ms ease-out',
        opacity:      isDimmed ? 0.35 : 1,
        zIndex:       isExpanded ? 1000 : undefined,
      }}
    >
      {/* Handles — all four sides for flexible edge routing */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />

      {/* Clause number + badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
        <span style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize:   11,
          fontWeight: 700,
          color:      domainColor,
          lineHeight: 1.2,
          flexShrink: 0,
        }}>
          {clause.clause_number}
        </span>
        {clause.cited && (
          <span style={{
            fontSize:     9,
            background:   'rgba(34,197,94,0.18)',
            color:        '#22c55e',
            borderRadius: 3,
            padding:      '1px 4px',
            fontWeight:   600,
          }}>
            cited
          </span>
        )}
        {isCut && (
          <span style={{
            fontSize:   9,
            color:      '#52525b',
            marginLeft: 'auto',
          }}>
            ✂ cut
          </span>
        )}
      </div>

      {/* Title */}
      <div style={{
        fontSize:     11,
        color:        '#d4d4dc',
        lineHeight:   1.3,
        marginBottom: 6,
        overflow:     'hidden',
        display:      '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        height:       '2.6em',
      }}>
        {clause.title || clause.clause_number}
      </div>

      {/* Relevance bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{
          flex:         1,
          height:       4,
          background:   '#232327',
          borderRadius: 2,
          overflow:     'hidden',
        }}>
          <div style={{
            width:        `${score}%`,
            height:       '100%',
            background:   domainColor,
            borderRadius: 2,
            opacity:      isCut ? 0.35 : 0.85,
            transition:   'width 0.3s ease',
          }} />
        </div>
        <span style={{
          fontSize:   9,
          color:      '#8b8b96',
          fontFamily: 'monospace',
          flexShrink: 0,
          minWidth:   26,
        }}>
          {score}%
        </span>
      </div>

      {/* ── Expanded panel (Tier 1) ── */}
      {isExpanded && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position:     'absolute',
            ...(panelSide.up ? { bottom: CLAUSE_H + 4 } : { top: CLAUSE_H + 4 }),
            ...(panelSide.right ? { right: 0 } : { left: 0 }),
            width:        320,
            maxHeight:    420,
            overflowY:    'auto',
            background:   '#1c1c1f',
            border:       `1.5px solid ${domainColor}`,
            borderRadius: 8,
            boxShadow:    '0 8px 32px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.4)',
            zIndex:       1000,
            padding:      '10px 12px',
            boxSizing:    'border-box',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: domainColor, flexShrink: 0 }}>
              {clause.clause_number}
            </span>
            <span style={{ fontSize: 10, color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {clause.standard}
            </span>
            <span style={{ fontSize: 10, background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>
              {score}%
            </span>
            <button
              onClick={e => { e.stopPropagation(); ctx.onExpandClause(null); }}
              style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 13, padding: '0 2px', flexShrink: 0 }}
              aria-label="Collapse"
            >✕</button>
          </div>

          {/* Provenance */}
          <div style={{ fontSize: 10, color: '#52525b', marginBottom: 8 }}>
            {provenanceLabel(clause.retrieval_source)}
            {clause.rank != null && ` · #${clause.rank} of retrieved`}
          </div>

          {/* Title */}
          <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500, marginBottom: 8, lineHeight: 1.4 }}>
            {detail.full?.title || clause.title}
          </div>

          {/* Body preview */}
          {detail.loading ? (
            <div style={{ marginBottom: 8 }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ height: 10, background: '#2a2a2e', borderRadius: 3, marginBottom: 5, width: i === 3 ? '60%' : '100%' }} />
              ))}
            </div>
          ) : detail.full ? (
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                overflow: 'hidden', maxHeight: detail.full.body.length > 600 ? 120 : undefined }}>
                {detail.full.body.slice(0, 600)}
              </div>
              {detail.full.body.length > 600 && (
                <>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 32,
                    background: 'linear-gradient(transparent, #1c1c1f)' }} />
                  <button
                    onClick={e => { e.stopPropagation(); ctx.onOpenReader?.(nodeId); }}
                    style={{ display: 'block', marginTop: 4, fontSize: 10, color: '#6366f1', background: 'none',
                      border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                  >
                    Read full clause ↗
                  </button>
                </>
              )}
            </div>
          ) : null}

          {/* Page thumbnail strip */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            {detail.loading ? (
              [1,2].map(i => <div key={i} style={{ width: 56, height: 74, background: '#2a2a2e', borderRadius: 4 }} />)
            ) : detail.pages && detail.pages.length > 0 ? (
              detail.pages.filter(p => p.has_image).slice(0, 3).map((p, i) => (
                <div
                  key={p.page_start}
                  onClick={e => { e.stopPropagation(); ctx.onOpenReader?.(nodeId, p.page_start); }}
                  style={{ cursor: 'pointer', borderRadius: 4, overflow: 'hidden', border: `1px solid color-mix(in srgb, ${domainColor} 40%, transparent)` }}
                >
                  <img
                    src={p.image_url ?? getPageImageUrl(p.pdf_id, p.page_start)}
                    alt={`Page ${p.page_start}`}
                    style={{ width: 56, height: 74, objectFit: 'cover', display: 'block' }}
                    loading="lazy"
                  />
                  <div style={{ fontSize: 9, color: '#52525b', textAlign: 'center', padding: '2px 0' }}>p.{p.page_start}{i > 0 ? ' ›' : ''}</div>
                </div>
              ))
            ) : detail.pages && detail.pages.length === 0 ? (
              <span style={{ fontSize: 10, color: '#52525b', fontStyle: 'italic' }}>No page images</span>
            ) : null}
          </div>

          {/* Phase 2c: Cross-ref chips — outgoing references from this clause */}
          {(() => {
            const outgoing = ctx.crossRefs.filter(x => x.from_clause_id === nodeId);
            if (!outgoing.length) return null;
            return (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: '#52525b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  References
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {outgoing.map(xref => {
                    const label = ctx.clauseLabels.get(xref.to_clause_id) ?? xref.to_clause_id;
                    const isGhost = ctx.ghostNodeIds.has(xref.to_clause_id);
                    return (
                      <button
                        key={xref.to_clause_id}
                        onClick={e => { e.stopPropagation(); ctx.onNavTo(xref.to_clause_id); }}
                        title={isGhost ? 'Navigate to ghost node' : 'Navigate to clause'}
                        style={{
                          fontSize:     9,
                          color:        isGhost ? '#64748b' : '#a5b4fc',
                          background:   isGhost ? 'rgba(100,116,139,0.08)' : 'rgba(99,102,241,0.12)',
                          border:       isGhost ? '1px dashed #374151' : '1px solid rgba(99,102,241,0.25)',
                          borderRadius: 3,
                          padding:      '2px 6px',
                          cursor:       'pointer',
                          fontFamily:   'ui-monospace, monospace',
                          maxWidth:     120,
                          overflow:     'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace:   'nowrap',
                        }}
                      >
                        → {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Action footer */}
          <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #2a2a2e', paddingTop: 8 }}>
            <button
              onClick={e => {
                e.stopPropagation();
                const cite = `${clause.standard}${clause.clause_number ? ' §' + clause.clause_number : ''}`;
                navigator.clipboard.writeText(cite).catch(() => {});
              }}
              style={{ fontSize: 10, color: '#64748b', background: 'none', border: '1px solid #2a2a2e',
                borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}
            >
              Copy cite
            </button>
            {detail.full && (
              <button
                onClick={e => { e.stopPropagation(); ctx.onOpenReader?.(nodeId); }}
                style={{ fontSize: 10, color: '#6366f1', background: 'none', border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}
              >
                Open reader ↗
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
});


/* ─── GhostNode — dashed phantom card for not_retrieved clauses (Phase 2c) ── */

// CSS injected once for ghost border pulse animation and materialise spinner
const GHOST_CSS = `
@keyframes rf-ghost-pulse {
  0%, 100% { opacity: 0.6; }
  50%       { opacity: 1.0; }
}
@keyframes rf-ghost-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.rf-ghost-loading {
  animation: rf-ghost-pulse 1.0s ease-in-out infinite;
}
`;

let _ghostCssInjected = false;
function ensureGhostCss() {
  if (_ghostCssInjected) return;
  _ghostCssInjected = true;
  const el = document.createElement('style');
  el.textContent = GHOST_CSS;
  document.head.appendChild(el);
}

const GhostNode = memo(function GhostNode({ data }: NodeProps<GhostRFNode>) {
  const { notRetrieved, domainColor, kind } = data;
  const nodeId = useNodeId() ?? 'ghost-unknown';
  const ctx    = useContext(FlowCtx);

  ensureGhostCss();

  const isLoading   = ctx.ghostLoadingId === nodeId;
  const isError     = ctx.ghostErrorId   === nodeId;
  const [hovered,   setHovered]   = useState(false);
  const [requested, setRequested] = useState(false);

  const referencedCount = notRetrieved.referenced_by.length;
  const plural          = referencedCount !== 1 ? 's' : '';

  // Visual state driven by load/error/hover
  const borderColor = isError ? '#ef4444' : domainColor;
  const opacity     = isLoading ? 1.0 : hovered ? 0.85 : 0.6;

  const handleMaterialise = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading || !notRetrieved.id) return;
    ctx.onMaterialise(nodeId, notRetrieved);
  }, [isLoading, notRetrieved, nodeId, ctx]);

  const handleRequest = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (requested) return;
    ctx.onRequestAbsent(nodeId, notRetrieved.standard);
    setRequested(true);
    setTimeout(() => setRequested(false), 3000);
  }, [requested, notRetrieved.standard, nodeId, ctx]);

  return (
    <div
      className={isLoading ? 'rf-ghost-loading' : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width:        GHOST_W,
        height:       GHOST_H,
        borderRadius: 6,
        border:       `1.25px dashed ${borderColor}`,
        background:   'rgba(15,23,42,0.85)',
        boxSizing:    'border-box',
        padding:      '5px 8px',
        position:     'relative',
        cursor:       'default',
        transition:   'border-color 200ms ease, opacity 180ms ease',
        opacity,
      }}
    >
      {/* Handles — left target, right source */}
      <Handle type="target" position={Position.Left}
        style={{ background: 'transparent', border: 'none', width: 4, height: 4 }} />
      <Handle type="source" position={Position.Right}
        style={{ background: 'transparent', border: 'none', width: 4, height: 4 }} />

      {/* Top-left glyph: ? for ghost, ⌀ for absent */}
      <span style={{
        position:   'absolute',
        top:        5,
        left:       8,
        fontSize:   12,
        fontFamily: 'ui-monospace, monospace',
        color:      domainColor,
        lineHeight: 1,
      }}>
        {kind === 'absent' ? '⌀' : '?'}
      </span>

      {/* Line 1: standard name (10px mono, ellipsis) */}
      <div style={{
        marginLeft:   18,
        fontSize:     10,
        fontFamily:   'ui-monospace, monospace',
        color:        domainColor,
        overflow:     'hidden',
        textOverflow: 'ellipsis',
        whiteSpace:   'nowrap',
        lineHeight:   1.3,
        marginBottom: 3,
      }}>
        {notRetrieved.standard}
      </div>

      {/* Line 2: title (ghost with title) or reference count */}
      <div style={{
        marginLeft:   18,
        fontSize:     9,
        color:        '#64748b',
        overflow:     'hidden',
        textOverflow: 'ellipsis',
        whiteSpace:   'nowrap',
        lineHeight:   1.3,
      }}>
        {kind === 'ghost' && notRetrieved.title
          ? notRetrieved.title
          : `Referenced · ${referencedCount} clause${plural}`}
      </div>

      {/* Ghost: + pill triggers materialise */}
      {kind === 'ghost' && notRetrieved.id && (
        <button
          onClick={handleMaterialise}
          title={isError ? 'Retry fetch' : 'Fetch and add to graph'}
          style={{
            position:       'absolute',
            bottom:         5,
            right:          6,
            width:          18,
            height:         18,
            borderRadius:   '50%',
            border:         `1px solid ${isError ? '#ef4444' : domainColor}`,
            background:     '#0f172a',
            color:          isError ? '#ef4444' : domainColor,
            fontSize:       11,
            cursor:         isLoading ? 'wait' : 'pointer',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            padding:        0,
            lineHeight:     1,
            transition:     'opacity 150ms ease, border-color 150ms ease',
          }}
        >
          {isLoading ? (
            <span style={{
              display:        'inline-block',
              width:          10,
              height:         10,
              borderRadius:   '50%',
              border:         `1.5px solid color-mix(in srgb, ${domainColor} 30%, transparent)`,
              borderTopColor: domainColor,
              animation:      'rf-ghost-spin 0.8s linear infinite',
            }} />
          ) : isError ? '↻' : '+'}
        </button>
      )}

      {/* Absent: Request → text button */}
      {kind === 'absent' && (
        <button
          onClick={handleRequest}
          style={{
            position:   'absolute',
            bottom:     4,
            right:      6,
            fontSize:   8,
            color:      requested ? '#22c55e' : '#64748b',
            background: 'none',
            border:     'none',
            cursor:     requested ? 'default' : 'pointer',
            padding:    0,
            lineHeight: 1,
            transition: 'color 200ms ease',
            whiteSpace: 'nowrap',
          }}
        >
          {requested ? 'Requested ✓' : 'Request →'}
        </button>
      )}
    </div>
  );
});

/* ─── StandardGroupNode ──────────────────────────────────────────────────── */

const StandardGroupNode = memo(function StandardGroupNode({ data, width, height }: NodeProps<GroupRFNode>) {
  const { standardName, domainColor, clauseCount, ghostCount } = data;

  return (
    <div
      style={{
        width:        width  ?? '100%',
        height:       height ?? '100%',
        borderRadius: 8,
        border:       `1.5px solid color-mix(in srgb, ${domainColor} 27%, transparent)`,
        background:   `color-mix(in srgb, ${domainColor} 5%, #101012)`,
        position:     'relative',
        boxSizing:    'border-box',
      }}
    >
      {/* Handles for edge connections */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: 'transparent', border: 'none', width: 6, height: 6 }}
      />

      {/* Standard label */}
      <div style={{
        position:      'absolute',
        top:           7,
        left:          GROUP_PAD_X,
        right:         GROUP_PAD_X,
        display:       'flex',
        alignItems:    'center',
        gap:           6,
        pointerEvents: 'none',
      }}>
        <span style={{
          height:       10,
          width:        3,
          borderRadius: 2,
          background:   domainColor,
          flexShrink:   0,
          display:      'inline-block',
        }} />
        <span style={{
          fontSize:      10,
          fontWeight:    700,
          color:         domainColor,
          fontFamily:    'ui-monospace, monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          overflow:      'hidden',
          textOverflow:  'ellipsis',
          whiteSpace:    'nowrap',
        }}>
          {standardName}
        </span>
        <span style={{
          fontSize:   9,
          color:      '#52525b',  // --ink-5
          marginLeft: 'auto',
          flexShrink: 0,
        }}>
          {clauseCount} clause{clauseCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Phase 2c: ghost count chip — shown when at least one ghost/absent references this group */}
      {ghostCount > 0 && (
        <div style={{
          position:   'absolute',
          bottom:     4,
          left:       GROUP_PAD_X,
          right:      GROUP_PAD_X,
          fontSize:   9,
          color:      '#52525b',
          borderTop:  '1px dashed #2a2a2e',
          paddingTop: 4,
          pointerEvents: 'none',
        }}>
          ◌ {ghostCount} referenced
        </div>
      )}

      {/* Needed so React Flow can calculate parent bounds */}
      <div style={{ width: '100%', height: '100%' }} />
    </div>
  );
});

/* ─── CrossRefEdge — normative solid animated, informative dashed static ─── */

// Normative flow animation via CSS keyframes injected once
const NORMATIVE_ANIMATION_STYLE = `
@keyframes rf-normative-flow {
  from { stroke-dashoffset: 20; }
  to   { stroke-dashoffset: 0; }
}
.rf-normative-edge {
  animation: rf-normative-flow 3s linear infinite;
  stroke-dasharray: 8 4;
}
`;

let _normativeStyleInjected = false;
function ensureNormativeStyle() {
  if (_normativeStyleInjected) return;
  _normativeStyleInjected = true;
  const el = document.createElement('style');
  el.textContent = NORMATIVE_ANIMATION_STYLE;
  document.head.appendChild(el);
}

function CrossRefEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  data,
}: EdgeProps<CrossRefRFEdge>) {
  ensureNormativeStyle();

  const isNormative = (data as CrossRefData | undefined)?.refType !== 'informative';

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 8,
  });

  if (isNormative) {
    // Dual-layer: glow layer (blurred outer) + crisp inner
    return (
      <>
        {/* Outer glow layer */}
        <path
          d={edgePath}
          fill="none"
          stroke="rgba(167,139,250,0.18)"
          strokeWidth={5}
          style={{ filter: 'blur(2px)', pointerEvents: 'none' }}
        />
        {/* Inner crisp animated line */}
        <BaseEdge
          id={id}
          path={edgePath}
          markerEnd={markerEnd}
          className="rf-normative-edge"
          style={{
            ...style,
            stroke:      'rgba(167,139,250,0.70)',
            strokeWidth: 1.75,
          }}
        />
      </>
    );
  }

  // Informative: static dashed
  return (
    <BaseEdge
      id={id}
      path={edgePath}
      markerEnd={markerEnd}
      style={{
        ...style,
        stroke:          'rgba(139,92,246,0.45)',
        strokeWidth:     1.25,
        strokeDasharray: '4 4',
      }}
    />
  );
}

/* ─── Node and edge type registrations ───────────────────────────────────── */

const nodeTypes = {
  clause:        ClauseNode,
  standardGroup: StandardGroupNode,
  query:         QueryNode,
  ghost:         GhostNode,   // Phase 2c
} as const;

const edgeTypes = {
  crossRef: CrossRefEdge,
} as const;

/* ─── Props (exact match to SigmaRetrievalMap / RetrievalMapPanel) ──────── */

interface Props {
  tree: RetrievalTree | null;
  isStreaming: boolean;
  onClauseSelect: (clauseId: string) => void;
  onGhostFetch?: (clauseId: string) => void;
  expandedGhostIds?: Set<string>;
  loadingGhostId?: string | null;
  onExpandNode?: (params: ExpandNodeParams) => void;
  highlightedNodeId?: string | null;
  onHoverClause?: (clauseId: string | null) => void;
  onRequestStandard?: (standardName: string) => void;
}

/* ─── Handle exposed by ref for external focus control ──────────────────── */

export interface ReactFlowRetrievalMapHandle {
  /** Pan to and highlight a clause node by its id */
  focusNode: (clauseId: string) => void;
  /** Pan to and expand (Tier 1 card) a clause node by its id */
  expandNode: (clauseId: string) => void;
}

/* ─── Inner component (must be inside ReactFlowProvider) ─────────────────── */

const ReactFlowRetrievalMapInner = forwardRef<ReactFlowRetrievalMapHandle, Props>(
  function ReactFlowRetrievalMapInner(
    {
      tree,
      isStreaming,
      onClauseSelect,
      onExpandNode,
      highlightedNodeId: externalHighlight,
      onHoverClause,
    }: Props,
    ref,
  ) {
    const { fitView, getNodes, setCenter } = useReactFlow();

    const [nodes, setNodes, onNodesChange] = useNodesState<AnyRFNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

    // Internal highlight state (set by focusNode or external prop)
    const [internalHighlight, setInternalHighlight] = useState<string | null>(null);
    const highlightedNodeId = internalHighlight ?? externalHighlight ?? null;

    const expandNodeImp = useCallback((clauseId: string) => {
      setExpandedNodeId(clauseId);
      const allNodes = getNodes();
      const node = allNodes.find(n => n.id === clauseId);
      if (node) {
        let absX = node.position.x + CLAUSE_W / 2;
        let absY = node.position.y + CLAUSE_H / 2;
        if (node.parentId) {
          const parent = allNodes.find(n => n.id === node.parentId);
          if (parent) { absX += parent.position.x; absY += parent.position.y; }
        }
        setCenter(absX, absY, { zoom: 1.4, duration: 400 });
      }
    }, [getNodes, setCenter]);

    /* Expose focusNode via ref ─────────────────────────────────────────── */
    useImperativeHandle(ref, () => ({
      focusNode(clauseId: string) {
        const allNodes = getNodes();
        const node = allNodes.find(n => n.id === clauseId);
        if (node) {
          const w = (node.measured?.width  as number | undefined) ?? CLAUSE_W;
          const h = (node.measured?.height as number | undefined) ?? CLAUSE_H;
          let absX = node.position.x + w / 2;
          let absY = node.position.y + h / 2;
          if (node.parentId) {
            const parent = allNodes.find(n => n.id === node.parentId);
            if (parent) { absX += parent.position.x; absY += parent.position.y; }
          }
          setCenter(absX, absY, { zoom: 1.4, duration: 400 });
        }
        setInternalHighlight(clauseId);
      },
      expandNode: expandNodeImp,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [getNodes, setCenter, expandNodeImp]);

    const onClauseSelectStable  = useCallback((id: string) => onClauseSelect(id), [onClauseSelect]);
    const onHoverClauseStable   = useCallback((id: string | null) => onHoverClause?.(id), [onHoverClause]);
    const onExpandNodeStable    = useCallback((p: ExpandNodeParams) => onExpandNode?.(p), [onExpandNode]);

    // Phase 2b — expand + reader state
    const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
    const [navStack, setNavStack] = useState<string[]>([]);
    const [readerState, setReaderState] = useState<{ clauseId: string; page?: number } | null>(null);

    // Phase 2c — ghost materialise state
    const [ghostLoadingId, setGhostLoadingId] = useState<string | null>(null);
    const [ghostErrorId,   setGhostErrorId]   = useState<string | null>(null);

    // Derived from tree: ghost node IDs + clause label map
    const ghostNodeIds = useMemo<Set<string>>(() => {
      if (!tree) return new Set<string>();
      return new Set((tree.not_retrieved ?? []).map((_nr, idx) =>
        makeGhostId(_nr, idx)
      ));
    }, [tree]);

    const clauseLabels = useMemo<Map<string, string>>(() => {
      if (!tree) return new Map<string, string>();
      const m = new Map<string, string>();
      for (const c of tree.clauses) {
        m.set(c.id, c.clause_number ? `${c.clause_number} ${c.title ?? ''}`.trim() : (c.title ?? c.id));
      }
      return m;
    }, [tree]);

    // Dimmed node IDs when a node is expanded
    const dimmedNodeIds = useMemo<Set<string>>(() => {
      if (!expandedNodeId || !tree) return new Set<string>();
      const connected = new Set<string>();
      connected.add(expandedNodeId);
      for (const xref of tree.cross_refs ?? []) {
        if (xref.from_clause_id === expandedNodeId) connected.add(xref.to_clause_id);
        if (xref.to_clause_id   === expandedNodeId) connected.add(xref.from_clause_id);
      }
      const dimmed = new Set<string>();
      for (const clause of tree.clauses) {
        if (!connected.has(clause.id)) dimmed.add(clause.id);
      }
      return dimmed;
    }, [expandedNodeId, tree]);

    const handleNavTo = useCallback((targetId: string) => {
      setNavStack(prev => expandedNodeId ? [...prev, expandedNodeId] : prev);
      setExpandedNodeId(targetId);
      const allNodes = getNodes();
      const node = allNodes.find(n => n.id === targetId);
      if (node) {
        let absX = node.position.x + CLAUSE_W / 2;
        let absY = node.position.y + CLAUSE_H / 2;
        if (node.parentId) {
          const parent = allNodes.find(n => n.id === node.parentId);
          if (parent) { absX += parent.position.x; absY += parent.position.y; }
        }
        setCenter(absX, absY, { zoom: 1.4, duration: 350 });
      }
    }, [expandedNodeId, getNodes, setCenter]);

    const handleBack = useCallback(() => {
      const stack = [...navStack];
      const prev = stack.pop();
      setNavStack(stack);
      setExpandedNodeId(prev ?? null);
      if (prev) {
        const allNodes = getNodes();
        const node = allNodes.find(n => n.id === prev);
        if (node) {
          let absX = node.position.x + CLAUSE_W / 2;
          let absY = node.position.y + CLAUSE_H / 2;
          if (node.parentId) {
            const parent = allNodes.find(n => n.id === node.parentId);
            if (parent) { absX += parent.position.x; absY += parent.position.y; }
          }
          setCenter(absX, absY, { zoom: 1.4, duration: 350 });
        }
      }
    }, [navStack, getNodes, setCenter]);

    /* Phase 2c: handleMaterialise — fetch + insert ghost as real clause node */
    const handleMaterialise = useCallback(async (nodeId: string, nr: GhostData['notRetrieved']) => {
      if (!nr.id) return;
      setGhostLoadingId(nodeId);
      try {
        const full = await fetchClauseFull(nr.id);
        // Find a reference node to position the materialised clause next to
        const refId = (nr.referenced_by ?? [])[0] ?? null;
        setNodes((prev: AnyRFNode[]) => {
          const allNodes = [...prev];
          // Compute absolute insert position near the referencing clause
          let insertX = 200;
          let insertY = 200;
          if (refId) {
            const refNode = allNodes.find(n => n.id === refId);
            if (refNode) {
              let absX = refNode.position.x;
              let absY = refNode.position.y;
              if (refNode.parentId) {
                const parent = allNodes.find(n => n.id === refNode.parentId);
                if (parent) { absX += parent.position.x; absY += parent.position.y; }
              }
              insertX = absX + CLAUSE_W + 24;
              insertY = absY;
            }
          }
          // Build a minimal RetrievalTreeClause from the fetched full clause
          const newClause: RetrievalTreeClause = {
            id:                full.id,
            domain:            full.skill ?? '',
            standard:          full.standard,
            clause_number:     full.clause_number ?? '',
            title:             full.title,
            rank:              0,
            rrf_score:         0,
            rerank_score:      null,
            retrieval_source:  'cross_ref_only',
            cited:             false,
          };
          const clauseNode: ClauseRFNode = {
            id:       full.id,
            type:     'clause',
            position: { x: insertX, y: insertY },
            data: {
              clause:      newClause,
              domainColor: getDomainColor(full.skill ?? ''),
              score:       0,
            },
            draggable:  true,
            selectable: true,
          };
          // Remove ghost node; add real clause node
          return allNodes.filter(n => n.id !== nodeId).concat(clauseNode) as AnyRFNode[];
        });
        // Remove ghost edges; optionally add a cross-ref edge from the referencing clause
        setEdges((prev: Edge[]) => {
          const filtered = prev.filter(e => e.target !== nodeId && e.source !== nodeId);
          if (refId) {
            filtered.push({
              id:     `materialised-${refId}-${full.id}`,
              source: refId,
              target: full.id,
              type:   'crossRef',
              data:   { refType: 'normative' } as CrossRefData,
              style:  { stroke: 'rgba(167,139,250,0.70)', strokeWidth: 1.75 },
              markerEnd: {
                type:  MarkerType.ArrowClosed,
                color: 'rgba(167,139,250,0.70)',
                width: 12,
                height: 12,
              },
            } as CrossRefRFEdge);
          }
          return filtered;
        });
        setGhostLoadingId(null);
        // Auto-expand the materialised clause
        setExpandedNodeId(full.id);
      } catch {
        setGhostLoadingId(null);
        setGhostErrorId(nodeId);
        setTimeout(() => setGhostErrorId(null), 1500);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setNodes, setEdges]);

    /* Phase 2c: handleRequestAbsent — fire-and-forget corpus request */
    const handleRequestAbsent = useCallback((_nodeId: string, standard: string) => {
      // Best effort — UI state (Requested ✓) is handled in GhostNode itself
      void requestPageMapping('absent-request', standard);
    }, []);

        /* Rebuild layout when tree changes ─────────────────────────────────── */
    const isFirstRenderRef = useRef(true);
    // Stable identity of the last tree we built a layout for.
    // We use query + clause count as a lightweight fingerprint so that
    // a cited-flag-only update (from onDone in App.tsx) does NOT trigger
    // a full layout rebuild — that would call setNodes/setEdges/setState
    // in rapid succession and exhaust React’s re-render budget (error #310).
    const prevTreeFingerprintRef = useRef<string | null>(null);
    useEffect(() => {
      if (!tree) {
        prevTreeFingerprintRef.current = null;
        setNodes([]);
        setEdges([]);
        return;
      }
      // Fingerprint: query text + clause count + not_retrieved count.
      // Any structural change (new query, different results) changes this;
      // a pure cited-flag update keeps the same fingerprint.
      const fingerprint = `${tree.query}:${tree.clauses.length}:${(tree.not_retrieved ?? []).length}`;
      if (fingerprint === prevTreeFingerprintRef.current) {
        // Only cited flags changed — patch existing nodes in-place
        // without triggering a full layout rebuild.
        setNodes((prev) =>
          prev.map((n) => {
            if (n.type !== 'clause') return n;
            const data = n.data as ClauseData;
            const fresh = tree.clauses.find((c) => c.id === data.clause.id);
            if (!fresh || fresh.cited === data.clause.cited) return n;
            return { ...n, data: { ...data, clause: { ...data.clause, cited: fresh.cited } } };
          })
        );
        return;
      }
      prevTreeFingerprintRef.current = fingerprint;
      const { nodes: newNodes, edges: newEdges } = buildLayout(tree);
      // New query — reset expand state and clear detail cache
      setExpandedNodeId(null);
      setNavStack([]);
      setReaderState(null);
      clauseDetailCache.clear();
      clearClauseDetailCache();
      setNodes(newNodes);
      setEdges(newEdges);
      // Fit view only on first render of a fresh query
      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        requestAnimationFrame(() => {
          fitView({ padding: 0.12, duration: 400 });
        });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree]);

    // Reset first-render flag when tree becomes null (new query)
    useEffect(() => {
      if (!tree) isFirstRenderRef.current = true;
    }, [tree]);

    /* Context value ─────────────────────────────────────────────────────── */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ctxValueFull covers all deps
    const ctxValueFull = useMemo<FlowCtxValue>(() => ({
      highlightedNodeId,
      expandedNodeId,
      dimmedNodeIds,
      onClauseSelect:   onClauseSelectStable,
      onExpandClause:   (id) => { setExpandedNodeId(id); if (!id) setReaderState(null); },
      onHoverClause:    onHoverClauseStable,
      onExpandNode:     onExpandNodeStable,
      onOpenReader:     (clauseId, page) => setReaderState({ clauseId, page }),
      navStack,
      onNavTo:          handleNavTo,
      // Phase 2c additions
      crossRefs:        tree?.cross_refs ?? [],
      ghostNodeIds,
      clauseLabels,
      onMaterialise:    handleMaterialise,
      onRequestAbsent:  handleRequestAbsent,
      ghostLoadingId,
      ghostErrorId,
    }), [highlightedNodeId, expandedNodeId, dimmedNodeIds, onClauseSelectStable, onHoverClauseStable, onExpandNodeStable, navStack, handleNavTo, tree, ghostNodeIds, clauseLabels, handleMaterialise, handleRequestAbsent, ghostLoadingId, ghostErrorId]);

    // Count cited vs cut nodes for footer caption
    const citedCount    = tree?.clauses.filter(c => c.cited).length ?? 0;
    const cutCount      = tree?.clauses.filter(c => c.rrf_score < 0.01 || c.retrieval_source === 'cross_ref_only').length ?? 0;
    const referencedCount = tree?.cross_refs?.length ?? 0;

    /* ── Loading state ────────────────────────────────────────────────────── */
    // ── Keyboard handler ─ MUST be declared before early returns to satisfy
    // React's rules of hooks (hook count must be identical across all renders).
    const handleMapKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (readerState) { setReaderState(null); return; }
        if (expandedNodeId) { setExpandedNodeId(null); return; }
      }
      if ((e.key === 'f' || e.key === 'F') && expandedNodeId) {
        setReaderState({ clauseId: expandedNodeId });
      }
      if ((e.key === 'c' || e.key === 'C') && expandedNodeId) {
        const clause = tree?.clauses.find(c => c.id === expandedNodeId);
        if (clause) {
          const cite = `${clause.standard}${clause.clause_number ? ' §' + clause.clause_number : ''}`;
          navigator.clipboard.writeText(cite).catch(() => {});
        }
      }
      if (e.key === 'Backspace' && navStack.length > 0) {
        e.preventDefault();
        handleBack();
      }
    }, [readerState, expandedNodeId, navStack, tree, handleBack]);

    if (!tree && isStreaming) {
      return (
        <div className="rf-retrieval-map rf-retrieval-map--loading" role="status"
          style={{ width: '100%', height: '100%', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#101012', borderRadius: 6 }}>
          <div className="retrieval-map-loading">
            <div className="retrieval-map-loading__spinner" />
            <span className="retrieval-map-loading__text">Planning retrieval…</span>
          </div>
        </div>
      );
    }

    /* ── Empty / idle state ────────────────────────────────────────────────── */
    if (!tree && !isStreaming) {
      return (
        <div className="rf-retrieval-map rf-retrieval-map--empty"
          role="region"
          aria-label="React Flow retrieval map (idle)"
          style={{ width: '100%', height: '100%', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#101012', borderRadius: 6 }}>
          <div className="retrieval-map-empty">
            <svg className="retrieval-map-empty__icon" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="7" height="9" rx="1.5"/>
              <rect x="15" y="3" width="7" height="9" rx="1.5"/>
              <rect x="8.5" y="14" width="7" height="7" rx="1.5"/>
              <line x1="5.5" y1="12" x2="12" y2="17.5"/>
              <line x1="18.5" y1="12" x2="12" y2="17.5"/>
            </svg>
            <p>Run a query to see the retrieval map</p>
            <span>Standard groups · dagre layout · reranked clauses</span>
          </div>
        </div>
      );
    }

    return (
      <FlowCtx.Provider value={ctxValueFull}>
        <div
          className="rf-retrieval-map rf-retrieval-map--active"
          role="region"
          aria-label={`Retrieval map: ${tree?.clauses.length ?? 0} clauses across ${tree?.domains.length ?? 0} domains`}
          style={{ width: '100%', height: '100%', minHeight: 300, position: 'relative' }}
          tabIndex={0}
          onKeyDown={handleMapKeyDown}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            colorMode="dark"
            fitView
            fitViewOptions={{ padding: 0.12 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            zoomOnScroll={true}
            style={{ background: '#101012', borderRadius: 6 }}  // --bg-0
            proOptions={{ hideAttribution: true }}
          >
            {/* Background dot grid — --line token, gap 22, size 1 */}
            <Background
              variant={BackgroundVariant.Dots}
              gap={22}
              size={1}
              color="#2e2e33"
            />

            {/* Vignette overlay — indigo centre, dark corners (spec §1.1) */}
            <div
              style={{
                position:      'absolute',
                inset:         0,
                pointerEvents: 'none',
                zIndex:        1,
                background:    'radial-gradient(ellipse 75% 75% at 50% 45%, rgba(99,102,241,0.05) 0%, transparent 55%, rgba(0,0,0,0.35) 100%)',
                borderRadius:  6,
              }}
            />

            <Controls
              showInteractive={false}
              style={{ bottom: 44, left: 8 }}
            />
            {/* F39: minimap paints above the node layer — hide it while a node
                panel is expanded so the panel is never obscured */}
            {!expandedNodeId && <MiniMap
              nodeColor={(node) => {
                if (node.type === 'clause') {
                  return (node.data as ClauseData).domainColor;
                }
                if (node.type === 'query') {
                  return 'rgba(99, 102, 241, 0.9)';
                }
                return '#1c1c1f';
              }}
              maskColor="rgba(0,0,0,0.7)"
              style={{
                background:   '#161618',  // --bg-1
                border:       '1px solid #2e2e33',  // --line
                borderRadius: 6,
              }}
            />}
          </ReactFlow>

          {/* Footer caption — "Searched N domains · used top N · N below the line · N referenced" */}
          {tree && (
            <div
              className="rf-retrieval-map__footer retrieval-map-footer"
              style={{
                position:      'absolute',
                bottom:        4,
                left:          8,
                pointerEvents: 'none',
                zIndex:        10,
              }}
            >
              <div
                className="retrieval-map-legend"
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: '#8b8b96' }}
              >
                {/* Reranked dot */}
                {tree.meta?.reranked && (
                  <span
                    title="Results were reranked for relevance"
                    style={{
                      display:      'inline-block',
                      width:        6,
                      height:       6,
                      borderRadius: '50%',
                      background:   '#6366f1',
                      flexShrink:   0,
                    }}
                  />
                )}

                <span>
                  Searched {tree.domains.length} domain{tree.domains.length !== 1 ? 's' : ''}
                </span>
                <span style={{ color: '#3f3f46' }}>·</span>
                <span>
                  used top {tree.clauses.length - cutCount}
                </span>
                {cutCount > 0 && (
                  <>
                    <span style={{ color: '#3f3f46' }}>·</span>
                    <span>{cutCount} below the line</span>
                  </>
                )}
                {referencedCount > 0 && (
                  <>
                    <span style={{ color: '#3f3f46' }}>·</span>
                    <span>{referencedCount} referenced</span>
                  </>
                )}
                {citedCount > 0 && (
                  <>
                    <span style={{ color: '#3f3f46' }}>·</span>
                    <span style={{ color: '#22c55e' }}>{citedCount} cited</span>
                  </>
                )}
              </div>
            </div>
          )}
          {/* Breadcrumb back-stack */}
          {navStack.length > 0 && (
            <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={handleBack}
                style={{ fontSize: 11, color: '#a5b4fc', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}
              >
                ← Back
              </button>
              {navStack.slice(-2).map(id => {
                const clause = tree?.clauses.find(c => c.id === id);
                return clause ? (
                  <span key={id} style={{ fontSize: 10, color: '#52525b', background: '#1c1c1f', border: '1px solid #2a2a2e',
                    borderRadius: 4, padding: '2px 6px' }}>
                    {clause.standard}{clause.clause_number ? ` §${clause.clause_number}` : ''}
                  </span>
                ) : null;
              })}
            </div>
          )}

          {/* Tier 2 — Focused reader overlay */}
          {readerState && (() => {
            const cached = clauseDetailCache.get(readerState.clauseId);
            if (!cached?.full || !cached?.pages) return null;
            return (
              <ClauseReader
                clauseFull={cached.full}
                pages={cached.pages}
                initialPage={readerState.page}
                onClose={() => setReaderState(null)}
              />
            );
          })()}
        </div>
      </FlowCtx.Provider>
    );
  }
);

/* ─── Exported component (wraps in ReactFlowProvider, forwards ref) ──────── */

const ReactFlowRetrievalMap = forwardRef<ReactFlowRetrievalMapHandle, Props>(
  function ReactFlowRetrievalMap(props, ref) {
    const innerRef = useRef<ReactFlowRetrievalMapHandle>(null);

    useImperativeHandle(ref, () => ({
      focusNode(clauseId: string) {
        innerRef.current?.focusNode(clauseId);
      },
      expandNode(clauseId: string) {
        innerRef.current?.expandNode(clauseId);
      },
    }));

    return (
      <ReactFlowProvider>
        <ReactFlowRetrievalMapInner ref={innerRef} {...props} />
      </ReactFlowProvider>
    );
  }
);

export default ReactFlowRetrievalMap;
